// driveUpload.js — Phase 2 of GDRIVE-MIGRATION-PLAN.
// Client helper that orchestrates: get folder → init upload session → PUT bytes.
// All token handling stays in Edge Functions; client only deals with the
// presigned session URI Google issued.

import { supabase } from './supabase';
import { withRetry } from './retry';

export class DriveNotConnectedError extends Error {
  constructor() {
    super('Google Drive nie je pripojený. Pripoj ho v Nastaveniach.');
    this.name = 'DriveNotConnectedError';
  }
}

export class DriveAuthRevokedError extends Error {
  constructor() {
    super('Prístup k Drive bol zrušený. Pripoj Drive znovu v Nastaveniach.');
    this.name = 'DriveAuthRevokedError';
  }
}

function shouldRetryDriveError(err) {
  // Don't retry user-actionable errors — they need to fix Drive connection first
  if (err instanceof DriveNotConnectedError) return false;
  if (err instanceof DriveAuthRevokedError) return false;
  // Anything else (transient network, 5xx from Edge Function, Drive 503) is retriable
  return true;
}

/**
 * Uploads a file to the user's Google Drive under todue/{firma}/{year}/{type}.
 * Returns { gdrive_file_id, view_url }.
 *
 * @param {Object} opts
 * @param {File|Blob} opts.file
 * @param {string} opts.workspace_id
 * @param {number} opts.year - calendar year (e.g. 2026)
 * @param {'faktury'|'vydavky'} opts.type
 * @param {string|null} [opts.expense_id]
 * @param {string|null} [opts.invoice_id]
 * @param {(step: string) => void} [opts.onProgress] - inline status feedback
 */
export async function uploadFileToDrive(opts) {
  const { file, workspace_id, year, type, expense_id = null, invoice_id = null, onProgress } = opts;

  if (!workspace_id || !year || !type) {
    throw new Error('uploadFileToDrive requires workspace_id, year, type');
  }
  if (!(file instanceof Blob)) {
    throw new Error('uploadFileToDrive requires a File or Blob');
  }

  // Step 1 — resolve folder (creates hierarchy if needed)
  const folderId = await withRetry(async () => {
    const folderResp = await supabase.functions.invoke('get-or-create-drive-folder', {
      body: { workspace_id, year, type },
    });
    if (folderResp.error) {
      const msg = folderResp.error.message || '';
      const detail = await readErrorDetail(folderResp);
      if (detail?.error === 'drive_not_connected') throw new DriveNotConnectedError();
      if (detail?.error === 'drive_auth_revoked') throw new DriveAuthRevokedError();
      throw new Error(`get-or-create-drive-folder: ${msg || JSON.stringify(detail)}`);
    }
    const id = folderResp.data?.folder_id;
    if (!id) throw new Error('get-or-create-drive-folder returned no folder_id');
    return id;
  }, {
    label: 'drive-folder-resolve',
    shouldRetry: shouldRetryDriveError,
    onRetry: ({ attempt, total }) => {
      onProgress?.(`Pripojenie zlyhalo, skúšam znova (${attempt}/${total})...`);
    },
  });

  // Step 2 — POST file as multipart to drive-upload-file Edge Function.
  // Backend proxy pattern: avoids CORS on Drive's upload endpoint, keeps
  // session URI + access_token entirely server-side. File is forwarded
  // through Supabase Edge Function (max 6MB body — fine for typical SK
  // invoices/receipts).
  const fileName = (file.name || `upload-${Date.now()}`).replace(/[\r\n\\/]/g, '_');

  const result = await withRetry(async () => {
    const fd = new FormData();
    fd.append('file', file, fileName);
    fd.append('workspace_id', workspace_id);
    fd.append('parent_folder_id', folderId);
    fd.append('file_name', fileName);
    if (expense_id) fd.append('expense_id', expense_id);
    if (invoice_id) fd.append('invoice_id', invoice_id);

    const uploadResp = await supabase.functions.invoke('drive-upload-file', {
      body: fd,
      // Note: do NOT set Content-Type header — supabase-js + browser will set
      // multipart/form-data; boundary=... automatically.
    });
    if (uploadResp.error) {
      const detail = await readErrorDetail(uploadResp);
      if (detail?.error === 'drive_not_connected') throw new DriveNotConnectedError();
      if (detail?.error === 'drive_auth_revoked') throw new DriveAuthRevokedError();
      if (detail?.error === 'folder_gone' || detail?.error === 'folder_trashed') {
        // Recursive retry — folder cache was stale, get-or-create will rebuild
        return uploadFileToDrive(opts);
      }
      throw new Error(`drive-upload-file: ${uploadResp.error.message || JSON.stringify(detail)}`);
    }
    const data = uploadResp.data;
    if (!data?.gdrive_file_id) throw new Error('drive-upload-file returned no gdrive_file_id');
    return data;
  }, {
    label: 'drive-upload-file',
    shouldRetry: shouldRetryDriveError,
    onRetry: ({ attempt, total }) => {
      onProgress?.(`Upload zlyhal, skúšam znova (${attempt}/${total})...`);
    },
  });

  return {
    gdrive_file_id: result.gdrive_file_id,
    view_url: result.view_url,
    file_size: result.file_size ?? (file.size || null),
  };
}

async function readErrorDetail(invokeResp) {
  // supabase.functions.invoke wraps non-2xx into error.context.body (a Response)
  try {
    const ctx = invokeResp?.error?.context;
    if (ctx?.body) {
      const text = typeof ctx.body === 'string' ? ctx.body : await ctx.text();
      return JSON.parse(text);
    }
  } catch (_) { /* ignore */ }
  return null;
}

export async function isDriveConnected() {
  const { data, error } = await supabase.rpc('has_drive_access');
  if (error) return false;
  return data === true;
}
